import { Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch.js';
import { Button } from './ui.jsx';

/**
 * The four steps of setting up a program, across the top of each of its pages:
 * terms, tracking, invites and prizes, publish. Every step is a link, so a
 * brand can go back to any of them; the bar only shows where they are and
 * what is done.
 *
 * A new program has no id yet, so only the first step can be opened.
 */
export const PROGRAM_STEPS = [
  { key: 'setup', label: 'Program setup', path: (id) => `/dashboard/brand/programs/${id}` },
  { key: 'tracking', label: 'Tracking', path: (id) => `/dashboard/brand/programs/${id}/actions` },
  { key: 'invites', label: 'Invites & prizes', path: (id) => `/dashboard/brand/programs/${id}/competition` },
  { key: 'publish', label: 'Publish', path: (id) => `/dashboard/brand/programs/${id}/publish` },
];

/** Whether anything reports sales to this program: a connected store or a pasted webhook. */
export function isTracked(integration) {
  return (
    (integration?.connections ?? []).some((c) => c.status === 'active') ||
    Object.values(integration?.platforms ?? {}).some((p) => p.connected)
  );
}

export function ProgramSteps({ programId, current, program }) {
  const integration = useFetch(programId ? `/programs/${programId}/integration` : null, {
    skip: !programId,
  });

  const done = {
    setup: Boolean(programId),
    tracking: isTracked(integration.data),
    invites: Boolean(program?.competition?.prizes?.length),
    publish: program?.status === 'active',
  };

  return (
    <nav aria-label="Program setup steps" className="mb-8">
      <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {PROGRAM_STEPS.map((step, i) => {
          const isCurrent = step.key === current;
          const isDone = !isCurrent && done[step.key];
          const open = Boolean(programId) || step.key === 'setup';
          const body = (
            <>
              <span
                aria-hidden="true"
                className={`flex size-6 shrink-0 items-center justify-center rounded-full border text-2xs font-semibold ${
                  isCurrent
                    ? 'border-accent bg-accent text-white'
                    : isDone
                      ? 'border-positive/40 bg-positive-soft text-positive'
                      : 'border-rule-strong bg-paper-raised text-ink-faint'
                }`}
              >
                {isDone ? '✓' : i + 1}
              </span>
              <span className={isCurrent ? 'font-medium text-ink' : 'text-ink-muted'}>
                {step.label}
                {isDone && <span className="sr-only"> (done)</span>}
              </span>
            </>
          );
          const className = `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            isCurrent ? 'border-accent bg-accent-soft' : 'border-rule bg-paper-raised'
          }`;

          return (
            <li key={step.key} aria-current={isCurrent ? 'step' : undefined}>
              {open && !isCurrent ? (
                <Link to={step.path(programId)} className={`${className} hover:bg-paper-sunken`}>
                  {body}
                </Link>
              ) : (
                <span className={`${className} ${open ? '' : 'opacity-60'}`}>{body}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** "Next: …" at the foot of a step's page. */
export function NextStep({ programId, after }) {
  const index = PROGRAM_STEPS.findIndex((s) => s.key === after);
  const next = PROGRAM_STEPS[index + 1];
  if (!next || !programId) return null;
  return (
    <div className="flex justify-end border-t border-rule pt-6">
      <Button to={next.path(programId)}>Next: {next.label} →</Button>
    </div>
  );
}
